import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Chip } from '@/components/Chip/Chip'
import { Button } from '@/components/ui/button'
import { AppRoutes } from '@/constants/appRoutes'
import { useStripe } from '@/hooks/Stripe/useStripe'
import { useUserInfoQuery } from '@/store/api/Users/Users.query'
import { selectCreditsFromStore } from '@/store/api/Users/Users.selector'
import { formatDate } from '@/utils/date'

export const SuscriptionPanel = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data: userData } = useUserInfoQuery()
  const stripeCustomerId = userData?.stripeCustomerId

  const credits = useSelector(selectCreditsFromStore)

  const plan = userData?.account.plan
  const planName = plan?.name
  const renewDate = plan?.currentPeriodEnd
  const isActivePlan = plan?.isActive

  const { generatePortalSession, generateCheckoutTopupSession } = useStripe()

  const [isLoadingPortalSession, setIsLoadingPortalSession] = useState(false)
  const [isLoadingCheckoutSession, setIsLoadingCheckoutSession] =
    useState(false)

  // If user has an active plan, show the manage subscription button
  const onClickPortalSession = async () => {
    if (!stripeCustomerId) return

    setIsLoadingPortalSession(true)

    try {
      const portalSession = await generatePortalSession(stripeCustomerId)

      if (portalSession?.url) {
        window.open(portalSession.url, '_blank', 'noopener,noreferrer')
      }
    } finally {
      setIsLoadingPortalSession(false)
    }
  }

  // If user has no active plan, show the subscribe button
  const onClickSubscribe = () => {
    navigate(AppRoutes.paymentPaywall)
  }

  // If user has no credits and has an active plan, show the add topup credits button
  const onClickAddTopupCredits = async () => {
    if (!stripeCustomerId) return

    setIsLoadingCheckoutSession(true)

    try {
      const topupSession = await generateCheckoutTopupSession(stripeCustomerId)

      if (topupSession?.url) {
        window.open(topupSession.url, '_blank', 'noopener,noreferrer')
      }
    } finally {
      setIsLoadingCheckoutSession(false)
    }
  }

  return (
    <div className="flex flex-row justify-between items-center gap-4 bg-baseColor rounded-xl p-4 mb-4 max-sm:flex-col">
      <div className="flex flex-col gap-2">
        <div className="flex flex-row items-center gap-2">
          <h2 className="text-lg font-medium">
            {planName || t('dashboard.billingAndInvoices.noActivePlan')}
          </h2>
          {isActivePlan && (
            <Chip
              variant="blue"
              text={t('dashboard.billingAndInvoices.active')}
            />
          )}
        </div>

        {renewDate && (
          <p className="text-sm text-neutralColor30">
            {t('dashboard.billingAndInvoices.renewsOn')}{' '}
            {formatDate(renewDate, 'MM/dd/yyyy')}
          </p>
        )}
      </div>

      <div className="flex flex-row gap-2">
        {isActivePlan && !credits && (
          <Button
            isLoading={isLoadingCheckoutSession}
            type="button"
            variant="secondary"
            onClick={onClickAddTopupCredits}
            size="lg"
            className="rounded-full"
          >
            {/* If user has no credits and has an active plan, show the add topup credits button */}
            {t('dashboard.billingAndInvoices.addCredits')}
          </Button>
        )}

        {isActivePlan ? (
          <Button
            isLoading={isLoadingPortalSession}
            type="button"
            variant="default"
            onClick={onClickPortalSession}
            size="lg"
            className="rounded-full"
          >
            {/* If user has an active plan, show the manage subscription button */}
            {t('dashboard.billingAndInvoices.manageSubscription')}
          </Button>
        ) : (
          <Button
            type="button"
            variant="default"
            onClick={onClickSubscribe}
            size="lg"
            className="rounded-full"
          >
            {/* If user has no active plan, show the subscribe button */}
            {t('dashboard.billingAndInvoices.subscribe')}
          </Button>
        )}
      </div>
    </div>
  )
}
